import React, { useEffect, useState, useCallback } from "react";
import { io } from "socket.io-client";
import { useSelector } from "react-redux";
import { SOCKET_URL } from "../config/env";

let socketInstance = null;
const listeners = new Set();
let globalIsConnected = false;
let globalNotification = null;
let registeredUserId = null;
let globalIsAdmin = false;
const gameUploadProgressListeners = new Map();

const notifyListeners = () => {
    listeners.forEach((listener) => {
        listener({
            isConnected: globalIsConnected,
            notification: globalNotification,
        });
    });
};

const getSocket = () => {
    if (!socketInstance) {
        socketInstance = io(SOCKET_URL, {
            transports: ["websocket"],
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
            timeout: 20000,
            withCredentials: true,
        });

        socketInstance.on("connect", () => {
            globalIsConnected = true;
            notifyListeners();
            if (registeredUserId) {
                socketInstance.emit("register_user", registeredUserId.toString());
                if (globalIsAdmin) {
                    socketInstance.emit("register_admin");
                }
            }
        });

        socketInstance.on("new_order_alert", (data) => {
            globalNotification = {
                message: `⚡ Net-Alert: New order #${data.orderId.slice(-8).toUpperCase()} from ${data.customer} ($${Number(data.total).toFixed(2)})`,
                type: "success",
                isAdminAlert: true,
                orderId: data.orderId,
                total: data.total,
            };
            notifyListeners();

            setTimeout(() => {
                globalNotification = null;
                notifyListeners();
            }, 8000);
        });

        socketInstance.on("connect_error", () => {
            globalIsConnected = false;
            notifyListeners();
        });

        socketInstance.on("disconnect", () => {
            globalIsConnected = false;
            notifyListeners();
        });

        socketInstance.on("order_status_update", (data) => {
            globalNotification = {
                message: data.message || "Order placed successfully!",
                type: data.success ? "success" : "error",
                orderId: data.orderId,
                total: data.total,
            };
            notifyListeners();

            setTimeout(() => {
                globalNotification = null;
                notifyListeners();
            }, 6000);
        });

        socketInstance.on("game_upload_progress", (data) => {
            if (!data?.sessionId) return;
            const listeners = gameUploadProgressListeners.get(data.sessionId);
            if (listeners) {
                listeners.forEach((fn) => fn(data));
            }
        });
    }
    return socketInstance;
};

export const subscribeGameUploadProgress = (sessionId, callback) => {
    if (!sessionId || !callback) return () => {};

    getSocket();

    if (!gameUploadProgressListeners.has(sessionId)) {
        gameUploadProgressListeners.set(sessionId, new Set());
    }
    gameUploadProgressListeners.get(sessionId).add(callback);

    return () => {
        const listeners = gameUploadProgressListeners.get(sessionId);
        if (!listeners) return;
        listeners.delete(callback);
        if (listeners.size === 0) {
            gameUploadProgressListeners.delete(sessionId);
        }
    };
};

export const useSocket = () => {
    const { user, isAuthenticated } = useSelector((state) => state.auth);
    const [state, setState] = useState({
        isConnected: globalIsConnected,
        notification: globalNotification,
    });

    useEffect(() => {
        const listener = (newState) => setState(newState);
        listeners.add(listener);

        const socket = getSocket();
        const userId = isAuthenticated && user ? (user._id || user.id) : null;

        if (userId) {
            registeredUserId = userId.toString();
            globalIsAdmin = user.role?.toLowerCase() === 'admin';
            if (globalIsConnected) {
                socket.emit("register_user", registeredUserId);
                if (globalIsAdmin) {
                    socket.emit("register_admin");
                }
            }
        } else {
            registeredUserId = null;
            globalIsAdmin = false;
        }

        return () => {
            listeners.delete(listener);
        };
    }, [user, isAuthenticated]);

    const dismissNotification = useCallback(() => {
        globalNotification = null;
        notifyListeners();
    }, []);

    return {
        socket: socketInstance,
        isConnected: state.isConnected,
        notification: state.notification,
        dismissNotification,
    };
};

export const SocketProvider = ({ children }) => {
    return <>{children}</>;
};
